import React, { useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Shield, Eye, EyeOff, AlertTriangle } from "lucide-react";

/**
 * Data Protection Modal
 * 
 * Requires users to acknowledge data sensitivity before viewing real data.
 * Options:
 *   1. View in Presentation Mode (masked data) - Safe for sharing
 *   2. View Real Data - Must acknowledge responsibility
 * 
 * This helps prevent accidental data exposure during screen shares.
 */
const DataProtectionModal = ({ 
  isOpen, 
  onConfirm, 
  onPresentationMode,
  userName = "User"
}) => {
  const [acknowledged, setAcknowledged] = useState(false);
  const [showWarning, setShowWarning] = useState(false);

  const handleViewRealData = () => {
    if (!acknowledged) {
      setShowWarning(true);
      return;
    }
    setAcknowledged(false);
    setShowWarning(false);
    onConfirm();
  };

  const handlePresentationClick = () => {
    setAcknowledged(false);
    setShowWarning(false);
    onPresentationMode();
  };

  return (
    <Dialog open={isOpen} onOpenChange={() => {}}>
      <DialogContent 
        className="sm:max-w-lg"
        onPointerDownOutside={(e) => e.preventDefault()}
        onEscapeKeyDown={(e) => e.preventDefault()}
      >
        <DialogHeader>
          <div className="flex items-center gap-3 mb-2">
            <div className="p-2 rounded-full bg-amber-100">
              <Shield className="h-6 w-6 text-amber-600" />
            </div>
            <DialogTitle className="text-xl">Data Protection Notice</DialogTitle>
          </div>
          <DialogDescription className="text-left text-slate-600">
            Welcome back, <strong>{userName}</strong>. This dashboard contains sensitive client data.
            Please select how you would like to proceed.
          </DialogDescription>
        </DialogHeader>

        <div className="mt-4 space-y-4">
          {/* Presentation Mode Option */}
          <button
            onClick={handlePresentationClick}
            className="w-full p-4 border-2 border-emerald-200 rounded-lg hover:border-emerald-400 hover:bg-emerald-50 transition-all text-left group"
            data-testid="presentation-mode-btn"
          >
            <div className="flex items-start gap-3">
              <div className="p-2 rounded-lg bg-emerald-100 group-hover:bg-emerald-200 transition-colors">
                <EyeOff className="h-5 w-5 text-emerald-600" />
              </div>
              <div>
                <h3 className="font-semibold text-emerald-800">Presentation Mode</h3>
                <p className="text-sm text-slate-600 mt-1">
                  <strong>Safe for screen sharing.</strong> Client names, emails, and amounts will be masked.
                </p>
                <p className="text-xs text-emerald-600 mt-2 font-medium">
                  Recommended for meetings &amp; demos
                </p>
              </div>
            </div>
          </button>

          {/* Real Data Option */}
          <div className="p-4 border-2 border-slate-200 rounded-lg">
            <div className="flex items-start gap-3">
              <div className="p-2 rounded-lg bg-slate-100">
                <Eye className="h-5 w-5 text-slate-600" />
              </div>
              <div className="flex-1">
                <h3 className="font-semibold text-slate-800">View Real Data</h3>
                <p className="text-sm text-slate-600 mt-1">
                  Shows actual client information. <strong>Not recommended</strong> if sharing your screen.
                </p>
                
                {/* Acknowledgment checkbox */}
                <div className="mt-4 flex items-start gap-3">
                  <Checkbox
                    id="acknowledge"
                    checked={acknowledged}
                    onCheckedChange={setAcknowledged}
                    className="mt-0.5"
                  />
                  <label 
                    htmlFor="acknowledge" 
                    className="text-sm text-slate-700 cursor-pointer leading-tight"
                  >
                    I confirm I am <strong>not</strong> sharing my screen and understand my responsibility 
                    to protect client data.
                  </label>
                </div>

                {/* Warning message */}
                {showWarning && !acknowledged && (
                  <div className="mt-3 flex items-center gap-2 text-amber-600 text-sm">
                    <AlertTriangle className="h-4 w-4 flex-shrink-0" />
                    <span>Please acknowledge your responsibility before proceeding.</span>
                  </div>
                )}

                <Button
                  onClick={handleViewRealData}
                  variant="outline"
                  className="mt-4 w-full"
                  data-testid="view-real-data-btn"
                >
                  Continue with Real Data
                </Button>
              </div>
            </div>
          </div>
        </div>

        <div className="mt-4 pt-4 border-t border-slate-100">
          <p className="text-xs text-slate-500 text-center">
            You can toggle Presentation Mode anytime from your profile menu (click your avatar).
          </p>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default DataProtectionModal;
